/**
 * 导演台：关键帧插值与导出 JSON（供 AI 管线读取）
 */

function sortKf(kfs) {
  return [...(kfs || [])].sort((a, b) => (a.time || 0) - (b.time || 0))
}

/** ease in-out cubic, u in [0,1] */
export function easeInOutCubic(u) {
  return u < 0.5 ? 4 * u * u * u : 1 - Math.pow(-2 * u + 2, 3) / 2
}

/**
 * @param {Array<{time:number,pos:number[],target:number[],fov?:number}>} keyframes
 * @param {number} tSec
 * @param {{ ease?: boolean }} opts
 */
export function interpolateShotKeyframes(keyframes, tSec, opts = {}) {
  const kfs = sortKf(keyframes)
  if (!kfs.length) return null
  const ease = opts.ease !== false
  let t = Math.max(0, tSec)
  const tEnd = kfs[kfs.length - 1].time || 0
  if (t >= tEnd) {
    const k = kfs[kfs.length - 1]
    return {
      pos: [...k.pos],
      target: [...k.target],
      fov: k.fov != null ? k.fov : 42
    }
  }
  if (t <= (kfs[0].time || 0)) {
    const k = kfs[0]
    return {
      pos: [...k.pos],
      target: [...k.target],
      fov: k.fov != null ? k.fov : 42
    }
  }
  let i = 0
  for (; i < kfs.length - 1; i++) {
    if (t < kfs[i + 1].time) break
  }
  const a = kfs[i]
  const b = kfs[i + 1]
  const span = (b.time || 0) - (a.time || 0) || 1
  let u = (t - (a.time || 0)) / span
  if (ease) u = easeInOutCubic(u)
  const lerp = (x0, x1) => x0 + (x1 - x0) * u
  return {
    pos: [lerp(a.pos[0], b.pos[0]), lerp(a.pos[1], b.pos[1]), lerp(a.pos[2], b.pos[2])],
    target: [
      lerp(a.target[0], b.target[0]),
      lerp(a.target[1], b.target[1]),
      lerp(a.target[2], b.target[2])
    ],
    fov: lerp(a.fov != null ? a.fov : 42, b.fov != null ? b.fov : 42)
  }
}

/**
 * @param {object} payload
 * @returns {object} 与 AI 生成器约定的结构
 */
export function buildDirectorExportPayload(payload) {
  const {
    version = 1,
    units = 'meter',
    shotAspect = '16:9',
    characters = [],
    sceneObjects = [],
    shotKeyframes = [],
    durationSec = 0,
    meta = {}
  } = payload || {}

  const frames = []
  const fps = 12
  const dur = Math.max(durationSec, 0.001)
  const n = Math.ceil(dur * fps)
  for (let i = 0; i <= n; i++) {
    const t = (i / n) * dur
    const cam = interpolateShotKeyframes(shotKeyframes, t, { ease: true })
    if (!cam) break
    frames.push({
      t,
      camera_position: { x: cam.pos[0], y: cam.pos[1], z: cam.pos[2] },
      camera_target: { x: cam.target[0], y: cam.target[1], z: cam.target[2] },
      fov: cam.fov,
      characters: characters.map(c => ({
        id: c.id,
        position: { x: c.x, y: c.y, z: c.z },
        rotation_yaw_deg: c.yawDeg,
        rotation_pitch_deg: c.pitchDeg != null ? c.pitchDeg : 0,
        height_m: c.height,
        label: c.label || '',
        ...(c.body_color ? { body_color: c.body_color } : {}),
        billboard: !!c.billboard,
        ...(c.pose && typeof c.pose === 'object' ? { limb_pose_deg: { ...c.pose } } : {})
      })),
      scene_objects: sceneObjects.map(o => ({
        id: o.id,
        type: o.type,
        position: { x: o.x, y: o.y, z: o.z },
        rotation_yaw_deg: o.yawDeg,
        rotation_pitch_deg: o.pitchDeg != null ? o.pitchDeg : 0,
        scale: { x: o.sx, y: o.sy, z: o.sz }
      }))
    })
  }

  return {
    version,
    units,
    shot_aspect: shotAspect,
    ai_prompt_hint:
      '请严格按照提供的相机参数与角色世界坐标渲染；保持透视与相对比例一致，禁止随意改变构图。',
    shot_keyframes: sortKf(shotKeyframes).map(k => ({
      time: k.time,
      camera_position: { x: k.pos[0], y: k.pos[1], z: k.pos[2] },
      camera_target: { x: k.target[0], y: k.target[1], z: k.target[2] },
      fov: k.fov != null ? k.fov : 42
    })),
    characters,
    scene_objects: sceneObjects,
    frame_sequence: frames,
    meta
  }
}

export function downloadJson(obj, filename) {
  const blob = new Blob([JSON.stringify(obj, null, 2)], { type: 'application/json' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename || 'director-scene.json'
  a.rel = 'noopener'
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}
